import Joi from 'joi';
import { AxiosError } from 'axios';
import api from '../api';

const bookingDocumentSchema = Joi.object({
  title: Joi.string().allow(''),
  file: Joi.string().uri().required(),
  type: Joi.string().required(),
  fileUuid: Joi.string().required(),
  bookings: Joi.array().items(Joi.number()).required(),
  isVisible: Joi.boolean().required(),
});

/**
 * Get booking documents List
 * @param {object} params - The query parameters.
 * @param {object} params.queryParams - The query parameters.
 * @param {string} params.bookingId - The booking id.
 * @returns {Promise<
 * {total: number, pageNumber: number, data: BookingDocument[]}>} - The list of booking docuemnts.
 */
export const getBookingDocuments = async ({ queryParams, bookingId }) => {
  // remove empty queryParams
  const cleanQueryParams = queryParams;
  Object.keys(cleanQueryParams).forEach(
    /**
     * @param {keyof typeof queryParams} key
     * @returns {void} - remove empty queryParams
     */
    (key) => {
      if (!cleanQueryParams[key]) {
        delete cleanQueryParams[key];
      }
    },
  );
  const result = await api.get(`/bookings/${bookingId}/documents`, { params: cleanQueryParams });

  try {
    const schema = Joi.object({
      data: Joi.array().items(bookingDocumentSchema),
      pageNumber: Joi.number(),
      total: Joi.number(),
    });

    await schema.validateAsync(result.data, { allowUnknown: true });
  } catch (/** @type {any} */ error) {
    throw new AxiosError('', `API response do not match booking document list Schema : ${error.message}`);
  }
  return result.data;
};

/**
 * Create a new booking documents
 * @param {{documents: BookingDocument[], bookingIds: number[]}} newDocuments
 * @returns {Promise<any>} - 201 return code
 */
export const createBookingDocuments = async (newDocuments) => {
  const result = await api.post('/bookings/documents', newDocuments);
  return result.status;
};

/**
 * Toggle booking document visibility
 * @param {object} params - The query parameters.
 * @param {{isVisible: boolean, bookingIds: number[]}} params.newVisibility
 * @param {string} params.documentUuid
 * @returns {Promise<any>} - 201 return code
 */
export const toggleDocumentVisibility = async ({ newVisibility, documentUuid }) => {
  const result = await api.patch(`/bookings/documents/${documentUuid}/visibility`, newVisibility);
  return result.status;
};

/**
 * Detach a booking document
 * @param {object} params - The query parameters.
 * @param {string} params.documentId - The document id.
 * @param {number[]} params.bookingIds - The booking ids.
 * @returns {Promise<number>} - 204 return code
 */
export const detachBookingDocument = async ({ documentId, bookingIds }) => {
  const result = await api.patch(`/documents/${documentId}`, { bookingIds });
  return result.status;
};
